import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';

/** What a failed AI run means for the screen. */
export type CoverAiRunError =
  /** 403 `M24_LIMIT_FEATURE`: the plan has no AI (Free, trial). */
  | 'noAi'
  /** 402 `M24_LIMIT_QUOTA`: fewer than 4 pictures are left this month. */
  | 'limit'
  /** 400 `M24_REFERENCE_RIGHTS_REQUIRED`: the artist must confirm the rights first. */
  | 'rights'
  /** 400 `M24_REFERENCE_INVALID`: a reference picture cannot be used. */
  | 'reference'
  /** 409 `M24_COVER_RUN_BUSY`: a run of this song still works. */
  | 'busy'
  /** 403 `M24_DEMO_READ_ONLY`: the example artist. */
  | 'demo'
  /** 429 `M24_RATE_LIMITED`: too many requests in a short time. */
  | 'rate'
  | 'failed';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

/** The `M24_*` code of an API error (`cause.code`, else `cause.reason`), or null. Pure. */
export function coverErrorCode(error: unknown): string | null {
  const cause = asRecord(asRecord(error)?.m24Limit);
  for (const value of [cause?.code, cause?.reason]) {
    if (typeof value === 'string' && value.startsWith('M24_')) {
      return value;
    }
  }
  return null;
}

/** The HTTP status of an API error, or null. Pure. */
export function coverErrorStatus(error: unknown): number | null {
  if (error instanceof FYMAPIError) {
    return error.statusCode;
  }
  const status = asRecord(error)?.statusCode;
  return typeof status === 'number' ? status : null;
}

/** Why the API refused the cover file of "Use as cover" (`M24_COVER_FILE_INVALID`, `cause.reason`). */
export type CoverFileRefusal = 'notImage' | 'notSquare' | 'tooSmall' | 'notOfAccount';
const COVER_FILE_REFUSALS: readonly CoverFileRefusal[] = ['notImage', 'notSquare', 'tooSmall', 'notOfAccount'];

/**
 * The reason of a `M24_COVER_FILE_INVALID` refusal, or null for any other error. A refusal with a
 * reason the app does not know reads as `notImage`. Pure.
 */
export function coverFileRefusal(error: unknown): CoverFileRefusal | null {
  const cause = asRecord(asRecord(error)?.m24Limit);
  if (cause?.code !== 'M24_COVER_FILE_INVALID') {
    return null;
  }
  const reason = COVER_FILE_REFUSALS.find((value) => value === cause.reason);
  return reason ?? 'notImage';
}

/** True when the API refused a design document (`M24_COVER_DOC_INVALID`): the same save fails again. Pure. */
export function isCoverDocRefusal(error: unknown): boolean {
  return coverErrorCode(error) === 'M24_COVER_DOC_INVALID';
}

/**
 * Maps the refusal of `POST .../ai-runs` to what the Picture panel shows (spec §5). The code
 * decides; a 402 or a 403 with no known code still reads as the limit or the plan. Pure.
 */
export function coverAiRunError(error: unknown): CoverAiRunError {
  const code = coverErrorCode(error);
  const status = coverErrorStatus(error);
  if (code === 'M24_REFERENCE_RIGHTS_REQUIRED') {
    return 'rights';
  }
  if (code === 'M24_REFERENCE_INVALID') {
    return 'reference';
  }
  if (code === 'M24_RATE_LIMITED' || status === 429) {
    return 'rate';
  }
  if (code === 'M24_COVER_RUN_BUSY' || status === 409) {
    return 'busy';
  }
  if (code === 'M24_DEMO_READ_ONLY') {
    return 'demo';
  }
  if (code === 'M24_LIMIT_QUOTA' || status === 402) {
    return 'limit';
  }
  if (code === 'M24_LIMIT_FEATURE' || status === 403) {
    return 'noAi';
  }
  return 'failed';
}
